import * as React from 'react';
import useClient from '../../../../hooks/useClient';
import Client from '../../../../models/client';
import BreadcrumbOptions from '../../../../models/enums/breadcrumbOptions';
import Paths from '../../../../models/enums/paths';
import ClientAdminMenu from './clientAdminMenu';
import * as styles from './client-admin.module.scss';
import { CircularProgress } from '@material-ui/core';
import { Switch, Route, useParams } from 'react-router-dom';
import Announcements from '../../announcements/announcements';
import ClientManager from './client-manager';
import ClientTags from '../tags/clientTags';
import Dashboards from '../dashboards/dashboards';
import { PagedDashboardProvider } from '../../../../shared/paginated-dashboard-context';
import AddDashboard from '../dashboards/manageDashboard/addDashboard';
import ClientNavigation from '../navigation/navigation';
import EditDashboard from '../dashboards/manageDashboard/editDashboard';
import { HelpDocumentsProvider } from '../../../../shared/help-documents-context';
import ClientHelpDocuments from '../../clientHelpDocuments/client-help-documents';
import AddClientDocument from '../../clientHelpDocuments/add-client-document';
import EditClientDocument from '../../clientHelpDocuments/edit-client-document';
import EditGlobalDocument from '../../clientHelpDocuments/edit-global-document';
import ManageRevisions from '../../clientHelpDocuments/manage-revisions';
import { PagedRevisionsProvider } from '../../../../shared/paginated-revisions';
import ClientUsersTable from '../clientUsers/client-users-table';
import { Breadcrumb, Breadcrumbs, SecondaryAlert } from 'kagr-ui';
import Snowflake from '../snowflake/snowflake';
import useUser from '../../../../hooks/useUser';

/**
 * AdminClient
 * Outer Component for the client management area, renders the header and secondary navigation, routes the secondary navigation views.
 */
const AdminClient = () => {
  const { clientId } = useParams<{ clientId: string }>();
  const [loading, setLoading] = React.useState<boolean>(true);
  const [client, setClient] = React.useState<Client>(null);
  const { fetchClient } = useClient();
  const breadcrumbs: Breadcrumb[] = [{ title: BreadcrumbOptions.Clients, path: Paths.Home }];
  const currentPath = Paths.EditClient.replace(':clientId', clientId);
  const [fetchError, setFetchError] = React.useState<string>('');

  const { getCurrentUser } = useUser();
  const [userRole, setUserRole] = React.useState<string>('');

  // Fetches the client information for the page on mount
  React.useEffect(() => {
    Promise.all([fetchClient(clientId), getCurrentUser()])
      .then(([client, user]) => {
        if (client) {
          setClient(client);
        } else {
          setFetchError(`Error: Failed to load client ${clientId}`);
        }
        setUserRole(user?.role);
        setLoading(false);
      })
      .catch((err) => {
        console.log(err);
        setFetchError(err);
      });
  }, []);

  if (loading) {
    return (
      <div className="loadingIndicator">
        <CircularProgress />
      </div>
    );
  } else if (fetchError) {
    return (
      <div>
        <SecondaryAlert type="error" message={fetchError} />
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      <div>
        <Breadcrumbs currentPage={<h1>{client.name}</h1>} breadcrumbs={breadcrumbs} />
        <ClientAdminMenu clientId={client.clientId} userRole={userRole} />
      </div>
      <div className={styles.main}>
        <Switch>
          <Route exact path={currentPath}>
            <ClientManager loading={loading} client={client} isNewClient={false} />
          </Route>
          <Route exact path={`${currentPath}/tags`}>
            <ClientTags
              clientId={client.clientId}
              assignedUsers={client.assignedUsers}
              assignedAdministrators={client.assignedAdministrators}
            />
          </Route>
          <Route exact path={`${currentPath}/navigation`}>
            <ClientNavigation clientId={client.clientId} />
          </Route>
          <Route exact={true} path={`${currentPath}/announcements`}>
            <Announcements client={client.clientId} />
          </Route>
          <Route exact={true} path={`${currentPath}/snowflake`}>
            <Snowflake clientId={client.clientId} />
          </Route>
          <Route path={`${currentPath}/dashboards`}>
            <PagedDashboardProvider>
              <Switch>
                <Route exact={true} path={`${currentPath}/dashboards`}>
                  <Dashboards clientId={Number(clientId)} />
                </Route>
                <Route exact={true} path={`${currentPath}/dashboards/create`}>
                  <AddDashboard clientId={Number(clientId)} />
                </Route>
                <Route exact={true} path={Paths.EditClientDashboard} component={EditDashboard} />
              </Switch>
            </PagedDashboardProvider>
          </Route>
          <Route path={Paths.ClientHelpDocuments}>
            <HelpDocumentsProvider>
              <Switch>
                <Route path={Paths.AddClientHelpDocument} exact component={AddClientDocument} />
                <Route path={`${Paths.EditClientHelpDocument}/global`} exact component={EditGlobalDocument} />
                <Route path={`${Paths.EditClientHelpDocument}/:revisions?`} exact component={EditClientDocument} />
                <Route path={Paths.ViewClientHelpDocumentRevisions} exact>
                  <PagedRevisionsProvider>
                    <ManageRevisions />
                  </PagedRevisionsProvider>
                </Route>
                <Route path={Paths.ClientHelpDocuments}>
                  <ClientHelpDocuments />
                </Route>
              </Switch>
            </HelpDocumentsProvider>
          </Route>
          <Route exact={true} path={Paths.ClientUsers.replace(':clientId', clientId)}>
            <ClientUsersTable
              assignedUsers={client.assignedUsers}
              assignedAdministrators={client.assignedAdministrators}
            />
          </Route>
        </Switch>
      </div>
    </div>
  );
};

export default AdminClient;
